import { ORIGINAL_GAME, ORIGINAL_GAMES } from '@archon3d/content';
import { validateGameDefinition, type GameDefinition } from '@archon3d/core';
import { useSyncExternalStore } from 'react';
import { accountRequest } from '../net/accounts';
import { copyEditableGame } from './copyGame';
import { readBrowserGames, updateBrowserGames } from './browserGameStorage';

export interface CatalogGame {
  definition: GameDefinition;
  source: 'Original' | 'Browser' | 'Own' | 'Public';
  owner?: string;
  author?: string;
  revision?: string;
}
export interface RemoteGame {
  id: string;
  owner: string;
  author: string;
  revision: string;
  definition: GameDefinition;
}
const listeners = new Set<() => void>();
// A just-saved browser copy is playable immediately. Refresh deliberately restores the
// authoritative account version so a conflict can be resolved by loading that version.
const previewOverrides = new Map<string, GameDefinition>();
// Resolve localStorage inside the reader so disabled storage is handled too.
let browserGames = readBrowserGames({ getItem: (key) => localStorage.getItem(key) });
let local = browserGames.games;
export function browserLibraryRecovery() {
  return browserGames;
}
let remote: { own: RemoteGame[]; public: RemoteGame[]; userId: string | null } = {
  own: [],
  public: [],
  userId: null,
};
/** Games the account library sent that this version cannot read, left out of the lists. */
let unreadableRemote = 0;
export function unreadableLibraryGames(): number {
  return unreadableRemote;
}
let snapshot: CatalogGame[] = [];
function publishSnapshot() {
  const own = new Set(remote.own.map((e) => e.id));
  const originals = [...ORIGINAL_GAMES].sort((a, b) => {
    if (a.id === 'hex-crown') return -1;
    if (b.id === 'hex-crown') return 1;
    return 0;
  });
  snapshot = [
    ...originals.map((definition) => ({ definition, source: 'Original' as const })),
    ...local
      .filter((g) => !own.has(g.id))
      .map((definition) => ({ definition, source: 'Browser' as const })),
    ...remote.own.map((e) => ({
      ...e,
      definition: previewOverrides.get(e.id) ?? e.definition,
      source: 'Own' as const,
    })),
    ...remote.public
      .filter(
        (e) => e.owner !== remote.userId && !own.has(e.id) && !local.some((g) => g.id === e.id),
      )
      .map((e) => ({ ...e, source: 'Public' as const })),
  ];
  for (const listener of listeners) listener();
}
publishSnapshot();
export function gameDefinition(id: string) {
  return snapshot.find((g) => g.definition.id === id)?.definition;
}
export function useGameLibrary() {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    () => snapshot,
  );
}
export function saveBrowserGame(definition: GameDefinition) {
  validateGameDefinition(definition);
  if (ORIGINAL_GAMES.some((g) => g.id === definition.id))
    throw new Error('The original game is read-only. Make a copy.');
  browserGames = updateBrowserGames(localStorage, (games) => [
    structuredClone(definition),
    ...games.filter((g) => g.id !== definition.id),
  ]);
  local = browserGames.games;
  previewOverrides.set(definition.id, structuredClone(definition));
  publishSnapshot();
}
export function removeBrowserGame(id: string) {
  browserGames = updateBrowserGames(localStorage, (games) => games.filter((g) => g.id !== id));
  local = browserGames.games;
  previewOverrides.delete(id);
  publishSnapshot();
}
export async function refreshGameLibrary() {
  const next = await accountRequest<typeof remote>('games');
  // One game this version cannot read, someone else's above all, must not hide the rest
  // (editor:quality#3): each is checked on its own, and those left out are counted and logged.
  const readable = (e: RemoteGame) => {
    try {
      validateGameDefinition(e.definition);
      return true;
    } catch (err) {
      console.warn(`Library game ${e.id} could not be loaded:`, (err as Error).message);
      return false;
    }
  };
  const own = next.own.filter(readable);
  const others = next.public.filter(readable);
  unreadableRemote = next.own.length + next.public.length - own.length - others.length;
  remote = { ...next, own, public: others };
  previewOverrides.clear();
  publishSnapshot();
  return remote;
}
export function publicCopies() {
  return remote.public.filter((e) => e.owner === remote.userId);
}
export function clearRemoteGameLibrary() {
  remote = { own: [], public: [], userId: null };
  unreadableRemote = 0;
  previewOverrides.clear();
  publishSnapshot();
}

export function newGameDefinition(
  name = 'My game',
  empty = true,
  source = ORIGINAL_GAME,
): GameDefinition {
  const g = copyEditableGame(source, name);
  if (empty) {
    g.board.cells = Array.from({ length: g.board.height }, () => '~'.repeat(g.board.width));
    g.board.placements = [];
    g.board.powerPoints = [];
    g.board.walls = [];
  }
  return g;
}
